import React from 'react';
import { ShieldCheck, Info } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { ProviderAccessCard } from './ProviderAccessCard';

/**
 * What this business lets its service provider do.
 *
 * <p>A page of its own rather than a row buried in Settings, because the control was
 * unfindable: a business could withdraw its provider's access only if it already knew
 * the setting existed. A nav entry IS the discoverability.
 *
 * <p>Intended to grow. This is where a business governs its relationship with its
 * provider — what they may do now, and later what it can ask them for.
 */
const ProviderAccess: React.FC = () => {
  const { user } = useAuth();
  const orgs: any[] = (user as any)?.organizations || [];
  const businesses = orgs.filter((o) => (o.type || '').toUpperCase() === 'BUSINESS');

  return (
    <div className="p-6 max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Provider access
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Whether the service provider that manages this account can see and change what is in it.
        </p>
      </div>

      {businesses.length === 0 ? (
        // A service provider has no provider above it. Saying so is better than an
        // empty page, which reads as something that failed to load.
        <div className="flex items-start gap-2 text-sm rounded-lg px-3 py-2.5 bg-blue-50 text-blue-900 border border-blue-200
                        dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-800">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            This setting belongs to a business. Your account is a service provider, so there is no
            provider above it to grant or withdraw access from.
          </span>
        </div>
      ) : (
        businesses.map((org) => (
          <ProviderAccessCard
            key={org.id}
            organizationId={org.id}
            organizationName={org.name}
            // The viewer is a member of this business — the same membership the
            // server checks, so the control matches what it will accept.
            canChange={true}
          />
        ))
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Withdrawing access takes effect immediately and everywhere — your provider can no longer
        read or change your agents, contacts, campaigns or settings. Every time they do access this
        business, it is recorded.
      </p>
    </div>
  );
};

export default ProviderAccess;
